import React, { memo } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { LIST_TABLE_SX } from '../../mathReports/mathTableStyles';
import { ACTION_BUTTON_SX } from '../../reports/summary/summaryStyles';
import { formatNumber, plural } from './revenueOps';
import type { RecalcSummary } from './revenueApi';

const BORDER = '1px solid #e2e8f0';
// В списке ошибок показываем первые таблицы, остальные сворачиваем в строку «и еще N»
const SHOWN_FAILED = 5;

const Stat: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <Box sx={{ p: 1.25, border: BORDER, borderRadius: '8px', backgroundColor: '#ffffff' }}>
    <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
      {formatNumber(value)}
    </Typography>
    <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>{label}</Typography>
  </Box>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontWeight: 600, color: '#334155', fontSize: '0.86rem', mb: 0.75 }}>{children}</Typography>
);

interface RecalcPreviewProps {
  summary: RecalcSummary | null;
  loading: boolean;
  error: string;
  onRetry: () => void;
}

const RecalcPreview: React.FC<RecalcPreviewProps> = ({ summary, loading, error, onRetry }) => {
  if (loading) {
    return (
      <Box sx={{ py: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
        <CircularProgress size={20} />
        <Typography sx={{ color: '#64748b', fontSize: '0.86rem' }}>Считаем, что изменится</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 1.5, border: '1px solid #fecaca', borderRadius: '8px', backgroundColor: '#fef2f2' }}>
        <Typography sx={{ color: '#b91c1c', fontSize: '0.86rem' }}>{error}</Typography>
        <Button size="small" variant="outlined" onClick={onRetry} sx={{ ...ACTION_BUTTON_SX, mt: 1 }}>
          Повторить
        </Button>
      </Box>
    );
  }

  if (!summary) return null;

  const hiddenFailed = summary.failedCount - Math.min(summary.failed.length, SHOWN_FAILED);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1.25 }}>
        <Stat value={summary.tables} label={`${plural(summary.tables, 'таблица', 'таблицы', 'таблиц')} просмотрено`} />
        <Stat value={summary.changedTables} label={`${plural(summary.changedTables, 'таблица', 'таблицы', 'таблиц')} с изменениями`} />
        <Stat value={summary.days} label={plural(summary.days, 'день изменится', 'дня изменится', 'дней изменится')} />
        <Stat value={summary.values} label={plural(summary.values, 'значение изменится', 'значения изменятся', 'значений изменится')} />
      </Box>

      {summary.values === 0 ? (
        <Typography sx={{ color: '#475569', fontSize: '0.88rem' }}>
          Значения расчетных полей уже совпадают с текущими формулами, пересчитывать нечего.
        </Typography>
      ) : (
        <>
          {summary.byField.length > 0 && (
            <Box>
              <SectionTitle>Что изменится по полям</SectionTitle>
              <Box sx={{ ...LIST_TABLE_SX, border: BORDER, borderRadius: '8px' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Поле</th>
                      <th className="right">Значений</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.byField.map((item) => (
                      <tr key={item.name}>
                        <td className="strong">{item.label}</td>
                        <td className="right">{formatNumber(item.count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Box>
          )}

          {summary.samples.length > 0 && (
            <Box>
              <SectionTitle>Примеры: было и станет</SectionTitle>
              <Box sx={{ ...LIST_TABLE_SX, border: BORDER, borderRadius: '8px' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Таблица</th>
                      <th>Дата</th>
                      <th>Поле</th>
                      <th className="right">Было</th>
                      <th className="right">Станет</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.samples.map((item) => (
                      <tr key={`${item.table}-${item.date}-${item.field}`}>
                        <td className="muted">
                          <code>{item.table}</code>
                        </td>
                        <td className="nowrap">{item.date.split('-').reverse().join('.')}</td>
                        <td>{item.label}</td>
                        <td className="right muted nowrap">{formatNumber(item.from)}</td>
                        <td className="right strong nowrap">{formatNumber(item.to)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Box>
          )}
        </>
      )}

      {summary.failedCount > 0 && (
        <Box sx={{ p: 1.5, border: '1px solid #fed7aa', borderRadius: '8px', backgroundColor: '#fff7ed' }}>
          <Typography sx={{ fontWeight: 600, color: '#9a3412', fontSize: '0.86rem' }}>
            Таблицы, которые не удастся пересчитать: {summary.failedCount}
          </Typography>
          {summary.failed.slice(0, SHOWN_FAILED).map((item) => (
            <Typography key={item.table} sx={{ color: '#7c2d12', fontSize: '0.8rem', mt: 0.25 }}>
              {item.table}: {item.message}
            </Typography>
          ))}
          {hiddenFailed > 0 && (
            <Typography sx={{ color: '#7c2d12', fontSize: '0.8rem', mt: 0.25 }}>и еще {hiddenFailed}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default memo(RecalcPreview);
